/**
 * Public floor-plan model contract; keeps `UppKonvaArea` (`kind` DTO) in sync with `UppKonvaStage` (Konva engine).
 */

import { Subscription } from 'rxjs';
import {
    installUppKonvaGroupedBridgeToArea,
    mergeUppFloorPlanTheme,
    UppKonvaStage,
    uppKonvaAreaWire,
    UPPKONVA_DARKTHEME,
    UPPKONVA_LIGHTTHEME,
    type UppFloorPlanExportPngOptions,
    type UppFloorPlanPreferences,
    type UppKonvaStageLiveArea,
} from '../stage/upp-konva-stage';

import { UppKonvaArea } from './upp-konva-area';

import type { UppKonvaDestroy } from './upp-konva-destroy';
import type { UppKonvaRefresh } from './upp-konva-refresh';
import type { UppKonvaAreaSource } from './upp-konva-source';
import type { UppKonvaEditMode } from './upp-konva-types';
import type { UppKonvaTheme } from './upp-konva-types';

/**
 * Live scene state bound to the `upp-konva` host (engine via `UppKonvaStage` and `floorPlanModel`).
 *
 * - `model.area`: live shapes and `kind` DTO.
 * - `model.sceneModel` (internal): same instance as the host’s internal floor plan.
 * - `mode` defaults to `VIEW`. Use `EDIT` for drag and the Konva transformer.
 * - After `area.source`, `applyAreaFromSource`, or `area.notifyPresentationDirty()`, the scene updates; `data`-only
 *   mutations via `shape.syncPresentationToStage()` do **not** fire structural `onModelChanged`. To delete rows:
 *   `UppKonvaShape.remove()`, `UppKonvaArea.removeShapes`, or `UppKonvaGroup.remove()`.
 * - The host syncs structural engine changes back to the public area (including tables and `data.shape`).
 * - The host’s `(onSelect)` delivers `area.shapes` instances aligned with the canvas.
 * - In `EDIT`, `pushUndoSnapshot` / `undo` operate on copies of the `area.source` DTO.
 *
 * @see ../component/upp-konva-component.ts
 */
export class UppKonvaModel implements UppKonvaDestroy {
    // --------------------------------------------------------------------------
    // 1. Lifecycle
    // --------------------------------------------------------------------------

    /**
     * Creates the model, the live area, and the internal Konva engine.
     *
     * @param area - Initial DTO (`width`, `height`, `shapes` with `kind` per row). Optional: `name`, `orientation`, `mode`,
     *     presentation preferences, etc.
     */
    constructor(area: UppKonvaAreaSource) {
        this._area = UppKonvaArea.from(area);
        this._stage = new UppKonvaStage(this._area.source, this._hostPresentationPartial());
        this._stage.mode = this._editMode;
        this._area.wireKonvaStage(this._stage);
        installUppKonvaGroupedBridgeToArea(this._stage.floorPlanModel, this._area);
        this._applyThemeToScene();
        this._lastStructuralShapesFp = this._area.shapesStructuralFingerprint;

        this._areaSourceChangedSub = this._area.onSourceChanged$.subscribe(() => {
            if (!this._area.sceneReplayToStageSuppressed()) {
                this._pushSceneFromPublicArea();
            }

            this._applyThemeToScene();

            const fp = this._area.shapesStructuralFingerprint;
            if (fp !== this._lastStructuralShapesFp) {
                this._lastStructuralShapesFp = fp;
                this.onModelChanged?.();
            }
        });
    }

    /**
     * Pushes {@link marginPx}, {@link viewZoom} and {@link pixelRatioMax} to the live canvas.
     * Needed after area updates: the public area DTO does not round-trip those host framing fields.
     */
    applyHostPresentationPreferences(): void {
        if (this._destroyed) {
            return;
        }
        this._stage.setPresentationPreferences(this._hostPresentationPartial());
    }

    /**
     * Unsubscribes and tears down shapes. Idempotent; call when destroying the component that owns the model.
     */
    doDestroy(): void {
        if (this._destroyed) {
            return;
        }

        this._destroyed = true;
        this._undoStack.length = 0;
        this._areaSourceChangedSub.unsubscribe();
        this._stage.doDestroy();
        this._area.doDestroy();
    }

    // --------------------------------------------------------------------------
    // 2. Properties (integrator & presentation)
    // --------------------------------------------------------------------------

    /**
     * CSS pixel margins between the host edge and the usable plan rectangle: `[top, right, bottom, left]`.
     */
    marginPx: [number, number, number, number] = [12, 12, 12, 12];
    /** Upper bound for `devicePixelRatio` applied to Konva canvases. */
    pixelRatioMax = 2;
    /** Multiplier (≥ 0.25) on the automatic framing scale of the plan. */
    viewZoom = 1;

    /**
     * Optional callback fired only when the structural shape list changes (`id` + `kind` order), not for purely visual
     * mutations or in-place geometry edits.
     */
    onModelChanged: (() => void) | null = null;

    /**
     * Live floor-plan area (same instance for the model’s lifetime).
     * To apply data: `applyAreaFromSource` or assign `area.source`.
     */
    get area(): UppKonvaArea {
        return this._area;
    }

    /**
     * Canvas interaction mode: `VIEW` (read-only / selection) or `EDIT` (transformer, drag).
     * Does not emit `onModelChanged`.
     */
    get mode(): UppKonvaEditMode {
        return this._editMode;
    }

    set mode(value: UppKonvaEditMode) {
        if (this._destroyed) {
            return;
        }
        this._editMode = value;
        this._stage.mode = value;
        this.doRefresh();
    }

    /** Theme preset (`UPPKONVA_*`); combinable with the `upp-konva` `[theme]` input. */
    get theme(): UppKonvaTheme | null {
        return this._theme;
    }

    set theme(value: UppKonvaTheme | null | undefined) {
        if (this._destroyed) {
            return;
        }
        this._theme = value === undefined ? null : value;
        this._applyThemeToScene();
        this.doRefresh();
    }

    /**
     * Presentation repaint counter from the engine (same value the `upp-konva` host uses for `[presentationEpoch]`).
     * Useful for diagnostics or demos; not part of the persisted plan contract.
     */
    get presentationEpoch(): number {
        return this._stage.presentationEpoch;
    }

    /** Whether at least one snapshot exists for `undo()`. */
    get canUndo(): boolean {
        return !this._destroyed && this._undoStack.length > 0;
    }

    // --------------------------------------------------------------------------
    // 3. Public methods (integrator)
    // --------------------------------------------------------------------------

    /**
     * Replaces area contents from a DTO without changing the `model.area` reference (assigns `area.source` internally).
     *
     * @param source - Full `UppKonvaAreaSource`.
     *
     * @remarks `onModelChanged` fires only when the structural shape list changes (`id` + `kind` order). Room-only or
     *     orientation-only changes repaint without that signal.
     */
    applyAreaFromSource(source: UppKonvaAreaSource): void {
        if (this._destroyed) {
            return;
        }
        if (this._editMode === 'EDIT') {
            this.pushUndoSnapshot();
        }
        this._suppressEchoUndoPush = true;
        try {
            this._area.source = source;
        } finally {
            queueMicrotask(() => {
                this._suppressEchoUndoPush = false;
            });
        }
    }

    /**
     * Pushes a copy of `area.source` for `undo()` (only in `EDIT`). Skips consecutive duplicates.
     * The `upp-konva` host calls this before applying geometry from the stage.
     */
    pushUndoSnapshot(): void {
        if (
            this._destroyed ||
            this._editMode !== 'EDIT' ||
            this._undoRestoring ||
            this._suppressEchoUndoPush
        ) {
            return;
        }
        const snap = this._cloneSourceForUndo(this._area.source);
        const fp = this._fingerprintSource(snap);
        const last = this._undoStack[this._undoStack.length - 1];
        if (last !== undefined && this._fingerprintSource(last) === fp) {
            return;
        }
        this._undoStack.push(snap);
        if (this._undoStack.length > UppKonvaModel._UNDO_STACK_MAX) {
            this._undoStack.shift();
        }
    }

    /**
     * Restores the last snapshot pushed via `pushUndoSnapshot`.
     *
     * @returns `true` when a state was restored.
     */
    undo(): boolean {
        if (this._destroyed || this._undoStack.length === 0) {
            return false;
        }
        const prev = this._undoStack.pop();
        if (!prev) {
            return false;
        }
        this._undoRestoring = true;
        try {
            this._area.source = prev;
            this.doRefresh();
        } finally {
            queueMicrotask(() => {
                this._undoRestoring = false;
            });
        }
        return true;
    }

    /** Requests a canvas repaint without firing `onModelChanged`. */
    doRefresh(): void {
        if (this._destroyed) {
            return;
        }

        this._stage.doRefresh();
        this.uppKonvaRefresh?.doRefresh();
    }

    /**
     * Serializes the plan to versioned document JSON (includes `schemaVersion` when applicable).
     *
     * @returns JSON string; empty string if the model was already destroyed.
     */
    exportModel(): string {
        if (this._destroyed) {
            return '';
        }
        return this._stage.exportModel();
    }

    /**
     * Parses a JSON document and replaces area contents when valid.
     *
     * @param json - Document produced by `exportModel` or compatible with the package schema.
     * @returns `true` when applied successfully.
     */
    importModel(json: string): boolean {
        if (this._destroyed) {
            return false;
        }
        const r = this._stage.importModel(json);
        if (!r.ok) {
            return false;
        }

        this._undoStack.length = 0;
        this._area.withSceneReplayToStageSuppressed(() => {
            this._area.source = uppKonvaAreaWire(this._stage.area as UppKonvaStageLiveArea);
        });

        return true;
    }

    /**
     * Exports a PNG snapshot of the mounted canvas.
     *
     * @param options - Engine export options (resolution, etc.).
     * @returns Image `Blob`, or `null` if the stage is not ready or the model was destroyed.
     */
    exportToPng(options?: UppFloorPlanExportPngOptions): Promise<Blob | null> {
        if (this._destroyed) {
            return Promise.resolve(null);
        }

        return this._stage.exportToPng(options);
    }

    // --------------------------------------------------------------------------
    // 4. Public fields / accessors — package internal (@internal)
    // --------------------------------------------------------------------------

    /**
     * Host hook for repaint. Package-internal (omitted from public TypeDoc).
     *
     * @internal
     */
    uppKonvaRefresh: UppKonvaRefresh | null = null;

    /**
     * Internal engine scene model. For debugging or advanced extensions only; integrators rely on `area` and this class’s
     * public methods.
     *
     * @internal
     */
    get sceneModel() {
        return this._stage.floorPlanModel;
    }

    // --------------------------------------------------------------------------
    // 5. Private members
    // --------------------------------------------------------------------------

    private _destroyed = false;
    private readonly _area: UppKonvaArea;
    private readonly _stage: UppKonvaStage;
    private _editMode: UppKonvaEditMode = 'VIEW';
    private _theme: UppKonvaTheme | null = null;
    private readonly _areaSourceChangedSub: Subscription;
    /** `id`/`kind` fingerprint of `area.shapes` on the last emission where `onModelChanged` ran (structural changes only). */
    private _lastStructuralShapesFp = '';

    private readonly _undoStack: UppKonvaAreaSource[] = [];
    private static readonly _UNDO_STACK_MAX = 50;
    /** Skips stacking while undo replays the same stage snapshot. */
    private _undoRestoring = false;
    /** Avoids a second push when the stage `(onChange)` echo follows {@link applyAreaFromSource} in the same turn. */
    private _suppressEchoUndoPush = false;

    private _hostPresentationPartial(): Partial<UppFloorPlanPreferences> {
        const margins = this.marginPx.filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
        const marginPx = margins.length > 0 ? Math.min(...margins) : 12;
        return {
            marginPx,
            pixelRatioMax: this.pixelRatioMax,
            viewZoom: this.viewZoom,
        };
    }

    /** Public DTO snapshot toward the engine, preserving stage `onGrouped` when present. */
    private _scenePayloadFromPublicArea(): UppKonvaStageLiveArea {
        const base: UppKonvaAreaSource = {
            ...this._area.source,
            shapes: [...this._area.source.shapes],
        };
        const snap = this._stage.area;
        return snap.onGrouped !== undefined ? { ...base, onGrouped: snap.onGrouped } : base;
    }

    private _pushSceneFromPublicArea(): void {
        this._stage.area = this._scenePayloadFromPublicArea();
        // Area DTO omits margin/zoom/pixelRatio; re-apply host values so they are not reset to defaults.
        this._stage.setPresentationPreferences(this._hostPresentationPartial());
    }

    private _applyThemeToScene(): void {
        if (this._theme === null) {
            this._stage.theme = null;
            return;
        }

        const base = this._theme === 'UPPKONVA_DARKTHEME' ? UPPKONVA_DARKTHEME : UPPKONVA_LIGHTTHEME;
        this._stage.theme = mergeUppFloorPlanTheme(undefined, base);
    }

    private _cloneSourceForUndo(s: UppKonvaAreaSource): UppKonvaAreaSource {
        return JSON.parse(JSON.stringify(s)) as UppKonvaAreaSource;
    }

    private _fingerprintSource(s: UppKonvaAreaSource): string {
        return JSON.stringify(s);
    }
}
